// src/pages/CaseDetail/components/PhasesTimelineTab.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import StlViewerModal from './StlViewerModal';
import AssignUserModal from '../../cases-list/components/AssignUserModal';
import AssignPackageMilestoneModal from './AssignPackageMilestoneModal';
import notificationService from '../../../services/notificationService';
import { runDentistCaseSubmission } from '../../../services/caseDentistSubmissionService';
import IPRMilestoneModal from './IPRMilestoneModal';
import {
  applyMilestoneStatusChange,
  applyPhaseStatusChange,
  generateLotBatchNumber,
} from '../../../workflow/caseTimelinePhaseMutations';
import {
  fetchMilestoneTemplateActionsForRuntime,
  workflowActionAllowedForAnyRole,
  normalizeWorkflowUserRoles,
} from '../../../services/workflowRuntimeService';
import { fetchWorkflowActionDefinitions } from '../../../services/workflowAdminService';
import { fetchTemplateNameOptionsByMilestoneIds } from '../../../services/milestoneNameOptionsService';
import MilestoneNameChoiceField from '../../../components/milestones/MilestoneNameChoiceField';
import {
  assertValidMilestoneNameChoice,
  isMilestoneNameChoicePending,
  requiresMilestoneNameSelection,
  resolveMilestoneDisplayName,
} from '../../../lib/milestoneNameOptions';
import { isOperatorFamilyRole } from '../../../lib/operatorRoles';
import {
  getAssignOperatorModalCopy,
  getAssignOperatorRoleTypesForPhase,
  isInitialReviewPhase,
  getOperatorAssignmentMilestone,
} from '../../../lib/phaseOperatorAssignmentRoles';
import { getOutcomeDisplay } from '../../../lib/milestoneOutcomeUtils';
import { syncEmptyProductionBatchesModelRanges } from '../../../services/caseProductionPhasesService';
import { mapCasePriorityToBatchPriority } from '../../../lib/casePriority';
import {
  getEligibleOperatorRoleForPhaseName,
  isProductionAtelierPhaseForAssignment,
  userCanAssignProductionPhaseResponsible,
} from '../../../lib/phaseProductionResponsibleRoles';
import { WORKSHOP_OPERATOR_ROLE_LABELS } from '../../../lib/workshopOperatorView';
import { isCaseStlFile, toStlViewerFileEntry } from '../../../lib/caseFileDisplayLabels';
import {
  formatMissingPhotosSubmissionError,
  validateCaseSubmissionPhotos,
} from '../../../lib/dentistCaseSubmissionValidation';
import { useTranslation } from 'react-i18next';
import CaseWorkflowProgress from '../../../components/cases/CaseWorkflowProgress';
import {
  ADMINISTRATIVE_AGENT_ROLE_TYPE,
} from '../../../lib/phaseOperatorAssignmentRoles';
import {
  AssignmentLockConflictError,
  assignCaseOperatorRevisionRpc,
  assignWorkshopPhaseResponsibleRpc,
  formatAssignmentLockMessage,
  getActiveCaseAssignment,
  getSelfAssignLockHint,
  isAdministratorActor,
  isSelfAssignBlockedForCase,
  toastAssignmentLockConflict,
} from '../../../lib/caseAssignmentLock';
import {
  getCaseAssistantPath,
  isAdminAssistantRole,
} from '../../../lib/dentistSubmissionWizard';
import { openCrmStudioInNewTab, isDesignPhaseTimelineItem } from '../../../config/crmStudio';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getPhaseIcon = (type) =>
  ({
    submission: 'Upload',
    review: 'Search',
    design: 'Palette',
    production: 'Package',
    quality_check: 'CheckCircle',
    shipping: 'Truck',
    delivered: 'Home',
  }[type] || 'Circle');

const getPhaseStatusStyle = (status) =>
  ({
    completed: 'bg-success text-success-foreground',
    in_progress: 'bg-warning text-warning-foreground',
    pending: 'bg-muted text-muted-foreground',
    submitted: 'bg-info text-info-foreground',
    in_review: 'bg-primary text-primary-foreground',
    cancelled: 'bg-destructive text-destructive-foreground',
  }[status] || 'bg-muted text-muted-foreground');

const getGroup = (phase) => String(phase?.group ?? '');

const ACTION_BUTTON_STYLES = {
  set_completion_type:    'border-purple-400/60 text-purple-400 bg-purple-400/10 hover:bg-purple-400/20 hover:border-purple-400',
  open_stl_preview:       'border-sky-400/60 text-sky-400 bg-sky-400/10 hover:bg-sky-400/20 hover:border-sky-400',
  open_crm_studio:        'border-violet-500/60 text-violet-600 bg-violet-500/10 hover:bg-violet-500/20 hover:border-violet-500 dark:text-violet-300',
  assign_operator:        'border-blue-400/60 text-blue-400 bg-blue-400/10 hover:bg-blue-400/20 hover:border-blue-400',
  assign_self_case:       'border-blue-400/60 text-blue-400 bg-blue-400/10 hover:bg-blue-400/20 hover:border-blue-400',
  assign_prosthetist:     'border-violet-400/60 text-violet-400 bg-violet-400/10 hover:bg-violet-400/20 hover:border-violet-400',
  assign_package:         'border-amber-400/60 text-amber-400 bg-amber-400/10 hover:bg-amber-400/20 hover:border-amber-400',
  open_design_review:     'border-emerald-400/60 text-emerald-400 bg-emerald-400/10 hover:bg-emerald-400/20 hover:border-emerald-400',
  open_ipr_milestone:     'border-indigo-400/60 text-indigo-400 bg-indigo-400/10 hover:bg-indigo-400/20 hover:border-indigo-400',
  open_production_sheet:  'border-teal-400/60 text-teal-400 bg-teal-400/10 hover:bg-teal-400/20 hover:border-teal-400',
  add_milestone_note:     'border-orange-400/60 text-orange-400 bg-orange-400/10 hover:bg-orange-400/20 hover:border-orange-400',
  confirm_phase:          'border-green-400/60 text-green-600 bg-green-400/10 hover:bg-green-50 dark:hover:bg-green-950',
  reject_phase:           'border-red-400/60 text-red-600 bg-red-400/10 hover:bg-red-50 dark:hover:bg-red-950',
  submit_case:            'border-primary/60 text-primary bg-primary/10 hover:bg-primary/20',
  view_ipr_sheet:         'border-indigo-400/60 text-indigo-400 bg-indigo-400/10 hover:bg-indigo-400/20 hover:border-indigo-400',
  confirm_milestone:      'border-green-400/60 text-green-600 bg-green-400/10 hover:bg-green-400/20 hover:border-green-400 dark:text-green-400',
  reject_milestone:       'border-red-400/60 text-red-600 bg-red-400/10 hover:bg-red-400/20 hover:border-red-400 dark:text-red-400',
  _default:               'border-violet-500/50 text-violet-700 dark:text-violet-300 bg-violet-500/10 hover:bg-violet-500/20',
};

const actionButtonClass = (actionKey) =>
  `flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-all duration-150 active:scale-95 ${
    ACTION_BUTTON_STYLES[actionKey] || ACTION_BUTTON_STYLES._default
  }`;

const MODIFIER_BUTTON_CLASS =
  'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-all duration-150 active:scale-95 border-slate-400/60 text-slate-500 dark:text-slate-400 bg-slate-400/10 hover:bg-slate-400/20 hover:border-slate-400';

const EDITABLE_AFTER_SAVE_ACTIONS = new Set([
  'open_production_sheet',
  'assign_operator',
  'assign_self_case',
  'assign_prosthetist',
  'open_ipr_milestone',
]);

const normalizeVisibleRoleTypes = (roles) =>
  Array.isArray(roles) ? roles.filter((role) => typeof role === 'string' && role.trim() !== '') : [];

const canRoleViewCaseDetailItem = (userRole, visibleRoleTypes) => {
  if (userRole === 'administrator' || userRole === 'agent_administratif') return true;
  const normalizedRoles = normalizeVisibleRoleTypes(visibleRoleTypes);
  if (normalizedRoles.length === 0) return true;
  return normalizedRoles.includes(userRole);
};

// ─────────────────────────────────────────────────────────────────────────────

const PhasesTimelineTab = ({
  phases,
  caseData,
  userRole,
  userRoleTypes = [],
  caseId,
  currentUserId = null,
  currentUserFullName = null,
  onPhaseUpdated,
  caseFiles = [],
  crmStudioSettings = null,
  templateActionsByTemplateId: templateActionsFromParent,
  workflowProgress = null,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const effectiveRoleTypes = useMemo(() => {
    const fromProp = normalizeWorkflowUserRoles(userRoleTypes);
    if (fromProp.length) return fromProp;
    return normalizeWorkflowUserRoles(userRole);
  }, [userRole, userRoleTypes]);

  const submissionPhotoCheck = useMemo(
    () => validateCaseSubmissionPhotos(caseFiles),
    [caseFiles],
  );

  useEffect(() => {
    if (!currentUserId || !effectiveRoleTypes.includes(ADMINISTRATIVE_AGENT_ROLE_TYPE)) {
      setActiveSelfAssignment(null);
      setLoadingSelfAssignmentLock(false);
      return;
    }
    let cancelled = false;
    setLoadingSelfAssignmentLock(true);
    getActiveCaseAssignment(supabase, currentUserId, 'initial_review')
      .then((active) => {
        if (cancelled) return;
        if (isSelfAssignBlockedForCase(active, caseId)) setActiveSelfAssignment(active);
        else setActiveSelfAssignment(null);
      })
      .catch((err) => {
        console.error('[PhasesTimelineTab] get_active_case_assignment', err);
        if (!cancelled) setActiveSelfAssignment(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSelfAssignmentLock(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, caseId, effectiveRoleTypes, onPhaseUpdated]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [assigningCaseOperator, setAssigningCaseOperator] = useState(false);
  const [loadingSelfAssignmentLock, setLoadingSelfAssignmentLock] = useState(false);
  const [activeSelfAssignment, setActiveSelfAssignment] = useState(null);

  const isSelfAssignLocked = isSelfAssignBlockedForCase(activeSelfAssignment, caseId);
  const selfAssignLockHint = getSelfAssignLockHint(activeSelfAssignment);
  const selfAssignBusy = assigningCaseOperator || loadingSelfAssignmentLock;
  const selfAssignLockedClass = isSelfAssignLocked ? ' opacity-45 cursor-not-allowed' : '';

  const handleSelfAssignClick = useCallback(
    (handler) => {
      if (isSelfAssignLocked && activeSelfAssignment) {
        toastAssignmentLockConflict(
          toast,
          navigate,
          new AssignmentLockConflictError(
            formatAssignmentLockMessage(activeSelfAssignment),
            activeSelfAssignment,
          ),
        );
        return;
      }
      handler?.();
    },
    [isSelfAssignLocked, activeSelfAssignment, navigate],
  );
  const [updatingPhaseId, setUpdatingPhaseId] = useState(null);
  const [pendingStatus, setPendingStatus]   = useState(null);

  // Single state for inline confirm/reject phase block
  // Shape: { phase, milestone, type: 'confirm' | 'reject' }
  const [pendingConfirmPhase, setPendingConfirmPhase] = useState(null);

  const [completionTypeModalOpen, setCompletionTypeModalOpen] = useState(false);
  const [completionTypeMilestone, setCompletionTypeMilestone] = useState(null);
  const [completionTypePhase, setCompletionTypePhase]         = useState(null);
  const [milestoneNameChoiceModal, setMilestoneNameChoiceModal] = useState(null);
  const [pendingMilestoneNameChoice, setPendingMilestoneNameChoice] = useState('');

  const [stlViewerOpen, setStlViewerOpen]   = useState(false);
  const [stlViewerFiles, setStlViewerFiles] = useState([]);


  const [iprModalOpen,   setIprModalOpen]   = useState(false);
  const [iprMilestoneId, setIprMilestoneId] = useState(null);

  const [noteModalOpen,   setNoteModalOpen]   = useState(false);
  const [noteMilestoneId, setNoteMilestoneId] = useState(null);
  const [noteText,        setNoteText]        = useState('');
  const [savingNote,      setSavingNote]      = useState(false);

  const [assignModalOpen,      setAssignModalOpen]      = useState(false);
  const [assignModalPhase,     setAssignModalPhase]     = useState(null);
  const [assignModalMilestone, setAssignModalMilestone] = useState(null);

  const [assignOperatorModalOpen, setAssignOperatorModalOpen] = useState(false);
  const [assignOperatorMilestone, setAssignOperatorMilestone] = useState(null);
  const [assignOperatorPhase, setAssignOperatorPhase] = useState(null);

  const [assignPackageModalOpen, setAssignPackageModalOpen] = useState(false);
  const [assignPackagePhase,     setAssignPackagePhase]     = useState(null);
  const [assignPackageMilestone, setAssignPackageMilestone] = useState(null);

  const [assignPhaseResponsibleOpen, setAssignPhaseResponsibleOpen] = useState(false);
  const [assignPhaseResponsibleTarget, setAssignPhaseResponsibleTarget] = useState(null);

  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeUrl,       setIframeUrl]       = useState('');

  const [savedDesignUrl, setSavedDesignUrl]   = useState({}); // { [phaseId]: true }
const [savedAligners,  setSavedAligners]    = useState({}); // { [phaseId]: true }

  const [designUrlInputs, setDesignUrlInputs] = useState({});
  const [savingUrl,       setSavingUrl]       = useState(false);
  const [alignerInputs,   setAlignerInputs]   = useState({});
  const [savingAligners,  setSavingAligners]  = useState(false);

  const [workflowDefMap, setWorkflowDefMap] = useState({});
  const [templateActionsByTemplateId, setTemplateActionsByTemplateId] = useState({});
  const [templateNameOptionsByTemplateId, setTemplateNameOptionsByTemplateId] = useState({});
  const resolvedTemplateActions =
    templateActionsFromParent != null ? templateActionsFromParent : templateActionsByTemplateId;
  const [assignedPackageName, setAssignedPackageName] = useState(null);
  const [assignedOperatorName,    setAssignedOperatorName]    = useState(null);
const [assignedProsthetistName, setAssignedProsthetistName] = useState(null);
  const [savedActionsByMilestone, setSavedActionsByMilestone] = useState({});

  const markActionSaved = useCallback((milestoneId, actionKey) => {
    if (!milestoneId || !actionKey) return;
    setSavedActionsByMilestone((prev) => {
      const existing = prev[milestoneId] ? new Set(prev[milestoneId]) : new Set();
      existing.add(actionKey);
      return { ...prev, [milestoneId]: existing };
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Load workflow definitions
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchWorkflowActionDefinitions()
      .then((defs) => {
        const map = {};
        for (const d of defs) map[d.action_key] = d;
        setWorkflowDefMap(map);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!phases?.items?.length) {
      setTemplateNameOptionsByTemplateId({});
      return undefined;
    }

    const ids = new Set();
    phases.items.forEach((p) =>
      (p.milestones || []).forEach((m) => {
        if (m.milestone_template_id) ids.add(m.milestone_template_id);
      })
    );
    if (ids.size === 0) {
      setTemplateNameOptionsByTemplateId({});
      return undefined;
    }

    let cancelled = false;
    fetchTemplateNameOptionsByMilestoneIds([...ids])
      .then((map) => {
        if (!cancelled) setTemplateNameOptionsByTemplateId(map);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [phases]);

  // ─────────────────────────────────────────────────────────────────────────
  // Load template actions
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (templateActionsFromParent != null) return undefined;
    if (!phases?.items?.length) { setTemplateActionsByTemplateId({}); return undefined; }

    const ids = new Set();
    phases.items.forEach((p) =>
      (p.milestones || []).forEach((m) => {
        if (m.milestone_template_id) ids.add(m.milestone_template_id);
      })
    );
    if (ids.size === 0) { setTemplateActionsByTemplateId({}); return undefined; }

    let cancelled = false;
    (async () => {
      try {
        const pairs = await Promise.all(
          [...ids].map(async (tid) => {
            const rows = await fetchMilestoneTemplateActionsForRuntime(tid);
            rows.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            return [tid, rows];
          })
        );
        if (!cancelled) setTemplateActionsByTemplateId(Object.fromEntries(pairs));
      } catch (e) { console.error(e); }
    })();
    return () => { cancelled = true; };
  }, [phases, templateActionsFromParent]);

  // ─────────────────────────────────────────────────────────────────────────
  // Seed savedActionsByMilestone
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!phases?.items?.length) return;
    setSavedActionsByMilestone((prev) => {
      const next = { ...prev };
      phases.items.forEach((phase) => {
        (phase.milestones || []).forEach((m) => {
          if (!m.completed) return;
          const templateId = m.milestone_template_id;
          if (!templateId) return;
          const actions = resolvedTemplateActions[templateId] || [];
          actions.forEach((row) => {
            if (!EDITABLE_AFTER_SAVE_ACTIONS.has(row.action_key)) return;
            if (!next[m.id]) next[m.id] = new Set();
            next[m.id].add(row.action_key);
          });
        });
      });
      return next;
    });
  }, [phases, resolvedTemplateActions]);

  // ─────────────────────────────────────────────────────────────────────────
  // Pre-fill inline inputs
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!phases?.items?.length) return;

const urls = {}, aligners = {}, sUrl = {}, sAligners = {};
phases.items.forEach((p) => {
  if (p.design_review_url)      { urls[p.id]     = p.design_review_url;       sUrl[p.id]     = true; }
  if (p.total_aligners != null) { aligners[p.id] = String(p.total_aligners);  sAligners[p.id] = true; }
});
setDesignUrlInputs((prev) => ({ ...urls, ...prev }));
setAlignerInputs((prev)   => ({ ...aligners, ...prev }));
setSavedDesignUrl((prev)  => ({ ...sUrl, ...prev }));
setSavedAligners((prev)   => ({ ...sAligners, ...prev }));
  }, [phases]);

  useEffect(() => {
  if (!caseData?.assignedStaffId) { setAssignedOperatorName(null); return; }
   supabase
    .from('user_profiles')
    .select('full_name')
    .eq('id', caseData.assignedStaffId)
    .single()
    .then(({ data }) => { if (data?.full_name) setAssignedOperatorName(data.full_name); });
}, [caseData?.assignedStaffId]);

useEffect(() => {
  if (!caseData?.prosthetistId) { setAssignedProsthetistName(null); return; }
   supabase
    .from('user_profiles')
    .select('full_name')
    .eq('id', caseData.prosthetistId)
    .single()
    .then(({ data }) => { if (data?.full_name) setAssignedProsthetistName(data.full_name); });
}, [caseData?.prosthetistId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Package name
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!caseData?.package) { setAssignedPackageName(null); return; }
    supabase
      .from('commercial_packages')
      .select('package_name')
      .eq('id', caseData.package)
      .single()
      .then(({ data }) => { if (data?.package_name) setAssignedPackageName(data.package_name); });
  }, [caseData?.package]);

  // ─────────────────────────────────────────────────────────────────────────
  // Phase / milestone status changes
  // ─────────────────────────────────────────────────────────────────────────
  const handlePhaseStatusChange = useCallback(
    async (phaseId, newStatus, phaseOrder, group) => {
      if (readOnly) return;
      setUpdatingPhaseId(phaseId);
      try {
        await applyPhaseStatusChange(supabase, {
          caseId, caseData, phaseId, newStatus, phaseOrder, group,
          generateBatchNumber: generateLotBatchNumber,
          onPhaseUpdated,
        });
      } catch (err) {
        console.error(err);
        toast.error('Erreur lors de la mise à jour: ' + err.message);
      } finally {
        setUpdatingPhaseId(null);
      }
    },
    [caseId, caseData, onPhaseUpdated, readOnly]
  );

  const tryConfirmDesignMilestone = useCallback(
  async (phaseId, urlSaved, alignerSaved) => {
    if (!urlSaved || !alignerSaved) return;
    const phase = phases?.items?.find(p => p.id === phaseId);
    if (!phase) return;
    const jalon1 = (phase.milestones ?? []).find(m => m.milestone_order === 1);
    if (!jalon1 || jalon1.completed) return;
    const now = new Date().toISOString();
    await supabase.from('phase_milestones').update({
      is_completed: true, completed_date: now,
      is_rejected: false, rejected_date: null, updated_at: now,
    }).eq('id', jalon1.id);
    markActionSaved(jalon1.id, 'open_design_review'); // so "Modifier" appears
    if (onPhaseUpdated) onPhaseUpdated();
  },
  [phases, markActionSaved, onPhaseUpdated]
);

  const handleMilestoneStatusChange = useCallback(
    async (milestoneId, newStatus, phase, milestoneMeta = null) => {
      if (readOnly) return;
      await applyMilestoneStatusChange(supabase, {
        milestoneId, newStatus, phase, getGroup,
        runPhaseStatusChange: handlePhaseStatusChange,
        onPhaseUpdated,
        milestoneMeta,
      });
    },
    [handlePhaseStatusChange, onPhaseUpdated, readOnly]
  );

  const openMilestoneNameChoiceIfNeeded = useCallback((milestone, phase, onConfirmed) => {
    const options = milestone?.milestone_template_id
      ? templateNameOptionsByTemplateId[milestone.milestone_template_id] || []
      : [];
    if (
      requiresMilestoneNameSelection(options) &&
      isMilestoneNameChoicePending(milestone, options)
    ) {
      setPendingMilestoneNameChoice('');
      setMilestoneNameChoiceModal({ milestone, phase, options, onConfirmed });
      return true;
    }
    return false;
  }, [templateNameOptionsByTemplateId]);

  // Direct milestone-only — no phase cascade
  const handleMilestoneOnlyStatusChange = useCallback(
    async (milestoneId, newStatus) => {
      const now = new Date().toISOString();
      const isCompleted = newStatus === 'completed';
      const { error } = await supabase
        .from('phase_milestones')
        .update({
          is_completed:   isCompleted,
          completed_date: isCompleted ? now : null,
          is_rejected:    !isCompleted,
          rejected_date:  !isCompleted ? now : null,
          updated_at:     now,
        })
        .eq('id', milestoneId);
      if (error) { toast.error('Erreur lors de la mise à jour du jalon'); return; }
      toast.success(isCompleted ? 'Jalon validé avec succès' : 'Jalon rejeté');
      if (onPhaseUpdated) onPhaseUpdated();
    },
    [onPhaseUpdated]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // STL
  // ─────────────────────────────────────────────────────────────────────────
  const getStlFiles = () =>
    (caseFiles || []).filter(isCaseStlFile).map(toStlViewerFileEntry);

  const handleOpenStlViewer = () => {
    const stlFiles = getStlFiles();
    if (!stlFiles.length) {
      toast.warning('Aucun fichier STL trouvé. Vérifiez l’onglet Fichiers (scans actifs).');
      return;
    }
    setStlViewerFiles(stlFiles);
    setStlViewerOpen(true);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Submit Case
  // ─────────────────────────────────────────────────────────────────────────
  const handleSubmitCase = useCallback(
    async (phaseId) => {
      if (!submissionPhotoCheck.ok) {
        toast.error(
          formatMissingPhotosSubmissionError(
            submissionPhotoCheck.missingKeys,
            submissionPhotoCheck.missingLabels,
          ),
        );
        return;
      }
      setIsSubmitting(true);
      try {
        await runDentistCaseSubmission({
          caseId,
          caseNumber: caseData?.caseNumber,
          phaseId,
        });
        toast.success('Conception soumise avec succès !');
        if (onPhaseUpdated) onPhaseUpdated();
      } catch (err) {
        toast.error('Erreur lors de la soumission : ' + (err?.message || err));
      } finally {
        setIsSubmitting(false);
      }
    },
    [caseId, caseData, onPhaseUpdated, submissionPhotoCheck],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Assign Prosthetist
  // ─────────────────────────────────────────────────────────────────────────
  const handleAssignProsthetist = useCallback(
    async (userId, fullName) => {
      if (!assignModalPhase || !assignModalMilestone) return;
      const now = new Date().toISOString();
      const { error: caseError } = await supabase.from('cases')
        .update({ prosthetist_id: userId, updated_at: now }).eq('id', caseId);
      if (caseError) throw new Error("Erreur assignation prothésiste: " + caseError.message);

      await notificationService.createNotification({
        userId, type: 'info', title: 'Cas assigné',
        message: `Un cas vous a été assigné`, link: `/case-detail/${caseId}`,
      });
      await supabase.from('case_timeline_phases')
        .update({ responsible_user_id: userId, updated_at: now })
        .eq('case_id', caseId).eq('group', '1').eq('phase_order', 3);
      await supabase.from('phase_milestones')
        .update({ is_completed: true, is_rejected: false, rejected_date: null, completed_date: now, updated_at: now })
        .eq('id', assignModalMilestone.id);



      markActionSaved(assignModalMilestone.id, 'assign_prosthetist');
       if (fullName) setAssignedProsthetistName(fullName)
      setAssignModalOpen(false);

     toast.success('Prothésiste assigné!');
        const allNowCompleted = (assignModalPhase?.milestones ?? []).every((m) =>
      m.id === assignModalMilestone.id ? true : m.completed === true
    );
    if (allNowCompleted) {
      await handleMilestoneStatusChange(assignModalMilestone.id, 'completed', assignModalPhase);
    } else {
      if (onPhaseUpdated) onPhaseUpdated();
    }
    },
    [assignModalPhase, assignModalMilestone, caseId, onPhaseUpdated, markActionSaved]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Assign Operator / self-assign (Révision initiale)
  // ─────────────────────────────────────────────────────────────────────────
  const assignCaseOperator = useCallback(
    async (userId, fullName, milestone, phase, actionKey = 'assign_operator', { force = false } = {}) => {
      if (!userId) {
        toast.error('Utilisateur non connecté');
        return;
      }
      if (!milestone?.id) {
        toast.error('Jalon introuvable pour l’assignation.');
        return;
      }
      const targetRoleTypes =
        actionKey === 'assign_self_case'
          ? [ADMINISTRATIVE_AGENT_ROLE_TYPE]
          : getAssignOperatorRoleTypesForPhase(phase);

      try {
        await assignCaseOperatorRevisionRpc(supabase, {
          caseId,
          userId,
          milestoneId: milestone.id,
          force,
          actorRoleTypes: effectiveRoleTypes,
          targetRoleTypes,
        });
      } catch (err) {
        if (err instanceof AssignmentLockConflictError) {
          toastAssignmentLockConflict(toast, navigate, err);
          throw err;
        }
        throw err;
      }

      await notificationService.createNotification({
        userId, type: 'info', title: 'Cas assigné',
        message: `Un cas vous a été assigné`, link: `/case-detail/${caseId}`,
      });

      markActionSaved(milestone.id, actionKey);
      toast.success('Opérateur assigné avec succès!');
      if (fullName) setAssignedOperatorName(fullName);
      if (actionKey === 'assign_self_case') setActiveSelfAssignment(null);
      if (onPhaseUpdated) onPhaseUpdated();
    },
    [caseId, onPhaseUpdated, markActionSaved, effectiveRoleTypes, navigate]
  );

  const handleAssignOperator = useCallback(
    async (userId, fullName, { force = false } = {}) => {
      if (!assignOperatorMilestone) return;
      try {
        await assignCaseOperator(
          userId,
          fullName,
          assignOperatorMilestone,
          assignOperatorPhase,
          'assign_operator',
          { force }
        );
        setAssignOperatorModalOpen(false);
        setAssignOperatorPhase(null);
      } catch (err) {
        if (!(err instanceof AssignmentLockConflictError)) {
          toast.error(err.message);
        }
        throw err;
      }
    },
    [assignOperatorMilestone, assignOperatorPhase, assignCaseOperator]
  );

  const handleAssignSelfCase = useCallback(
    async (milestone, phase) => {
      if (!currentUserId) {
        toast.error('Utilisateur non connecté');
        return;
      }
      const targetMilestone =
        isInitialReviewPhase(phase)
          ? getOperatorAssignmentMilestone(phase) ?? milestone
          : milestone;
      if (!targetMilestone?.id) {
        toast.error('Jalon « Opérateur assigné » introuvable.');
        return;
      }
      setAssigningCaseOperator(true);
      try {
        const active = await getActiveCaseAssignment(supabase, currentUserId, 'initial_review');
        if (isSelfAssignBlockedForCase(active, caseId)) {
          setActiveSelfAssignment(active);
          toastAssignmentLockConflict(
            toast,
            navigate,
            new AssignmentLockConflictError(formatAssignmentLockMessage(active), active),
          );
          return;
        }
        setActiveSelfAssignment(null);

        await assignCaseOperator(
          currentUserId,
          currentUserFullName,
          targetMilestone,
          phase,
          'assign_self_case'
        );
      } catch (err) {
        if (err instanceof AssignmentLockConflictError) {
          if (err.activeAssignment) setActiveSelfAssignment(err.activeAssignment);
          toastAssignmentLockConflict(toast, navigate, err);
        } else {
          toast.error(err.message || 'Erreur lors de l’assignation');
        }
      } finally {
        setAssigningCaseOperator(false);
      }
    },
    [currentUserId, currentUserFullName, assignCaseOperator, navigate, caseId]
  );

  const assignOperatorModalCopy = useMemo(
    () => getAssignOperatorModalCopy(assignOperatorPhase),
    [assignOperatorPhase]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Assign phase responsible (production atelier, groupe ≥ 2)
  // ─────────────────────────────────────────────────────────────────────────
  const handleAssignPhaseResponsible = useCallback(
    async (userId, { force = false } = {}) => {
      const phase = assignPhaseResponsibleTarget;
      if (!phase?.id || !userId) return;
      try {
        await assignWorkshopPhaseResponsibleRpc(supabase, {
          phaseId: phase.id,
          userId,
          caseId,
          force,
          actorRoleTypes: effectiveRoleTypes,
          targetRoleTypes: [getEligibleOperatorRoleForPhaseName(phase.name)].filter(Boolean),
        });

        await notificationService.createNotification({
          userId,
          type: 'info',
          title: 'Phase assignée',
          message: `Vous êtes responsable de la phase « ${phase.name} »`,
          link: `/case-detail/${caseId}`,
        });

        toast.success('Responsable de phase assigné');
        setAssignPhaseResponsibleOpen(false);
        setAssignPhaseResponsibleTarget(null);
        if (onPhaseUpdated) onPhaseUpdated();
      } catch (err) {
        if (err instanceof AssignmentLockConflictError) {
          toastAssignmentLockConflict(toast, navigate, err);
        } else {
          toast.error(err.message);
        }
        throw err;
      }
    },
    [assignPhaseResponsibleTarget, caseId, onPhaseUpdated, effectiveRoleTypes, navigate]
  );

  const handleClearPhaseResponsible = useCallback(
    async (phase) => {
      if (!phase?.id) return;
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('case_timeline_phases')
        .update({ responsible_user_id: null, updated_at: now })
        .eq('id', phase.id);
      if (error) throw new Error('Erreur lors du retrait du responsable : ' + error.message);
      toast.success('Responsable retiré');
      setAssignPhaseResponsibleOpen(false);
      setAssignPhaseResponsibleTarget(null);
      if (onPhaseUpdated) onPhaseUpdated();
    },
    [onPhaseUpdated]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Assign Package
  // ─────────────────────────────────────────────────────────────────────────
  const handlePackageAssigned = useCallback(
    async (packageId) => {
      if (!assignPackagePhase || !assignPackageMilestone) return;
      const now = new Date().toISOString();
      const { error: caseError } = await supabase.from('cases')
        .update({ package: packageId, updated_at: now }).eq('id', caseId);
      if (caseError) throw caseError;

      const { data: pkgData } = await supabase
        .from('commercial_packages').select('package_name').eq('id', packageId).single();
      if (pkgData?.package_name) setAssignedPackageName(pkgData.package_name);

      await supabase.from('phase_milestones')
        .update({ is_completed: true, is_rejected: false,completed_date: now, updated_at: now })
        .eq('id', assignPackageMilestone.id);

      const allNowCompleted = (assignPackagePhase?.milestones ?? []).every((m) =>
        m.id === assignPackageMilestone.id ? true : m.completed === true
      );

      setAssignPackageModalOpen(false);
      setAssignPackagePhase(null);
      setAssignPackageMilestone(null);

      toast.success('Package assigné et jalon validé!');
        if (onPhaseUpdated) onPhaseUpdated();
    },
    [assignPackagePhase, assignPackageMilestone, caseId, onPhaseUpdated]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Save note
  // ─────────────────────────────────────────────────────────────────────────
  const handleSaveNote = async () => {
    if (!noteText.trim()) { toast.warning('Veuillez entrer une note.'); return; }
    setSavingNote(true);
    try {
      const { error } = await supabase.from('phase_milestones')
        .update({ note: noteText.trim(), updated_at: new Date().toISOString() })
        .eq('id', noteMilestoneId);
      if (error) throw error;
      toast.success('Note enregistrée!');
      setNoteModalOpen(false); setNoteText(''); setNoteMilestoneId(null);
      if (onPhaseUpdated) onPhaseUpdated();
    } catch (err) {
      toast.error('Erreur: ' + err.message);
    } finally {
      setSavingNote(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Save Design Review URL
  // ─────────────────────────────────────────────────────────────────────────
  const handleSaveDesignUrl = async (phaseId) => {
    const url = designUrlInputs[phaseId]?.trim();
    if (!url) { toast.warning('Veuillez entrer une URL valide.'); return; }
    try { new URL(url); } catch { toast.error("L'URL saisie n'est pas valide."); return; }
    setSavingUrl(true);
    try {
      await supabase.from('case_timeline_phases')
        .update({ design_review_url: url, updated_at: new Date().toISOString() }).eq('id', phaseId);
      if (caseData?.dentistId) {
        await notificationService.createNotification({
          userId: caseData.dentistId, type: 'info', title: 'Conception disponible',
          message: `La conception du cas ${caseData?.caseNumber || caseId} est prête`,
          link: `/case-detail/${caseId}`,
        });
      }

setSavedDesignUrl(prev => ({ ...prev, [phaseId]: true }));
const nowAligner = savedAligners[phaseId] || 
  !!(phases?.items?.find(p => p.id === phaseId)?.total_aligners);
await tryConfirmDesignMilestone(phaseId, true, nowAligner);

      toast.success('Lien de révision enregistré!');
      if (onPhaseUpdated) onPhaseUpdated();
    } catch (err) {
      toast.error("Erreur: " + err.message);
    } finally {
      setSavingUrl(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Save Total Aligners
  // ─────────────────────────────────────────────────────────────────────────
  const handleSaveAligners = async (phaseId) => {
    const num = parseInt(alignerInputs[phaseId]);
    if (!num || num < 1) { toast.warning("Nombre d'aligneurs invalide."); return; }
    setSavingAligners(true);
    try {
      await supabase.from('case_timeline_phases')
        .update({ total_aligners: num, updated_at: new Date().toISOString() }).eq('id', phaseId);
      setSavedAligners(prev => ({ ...prev, [phaseId]: true }));
      const phase = phases?.items?.find((p) => p.id === phaseId);
      const nowUrl = savedDesignUrl[phaseId] || !!(phase?.design_review_url);
      await tryConfirmDesignMilestone(phaseId, nowUrl, true);

      // Initialise UP/LOW « À » (moitié des aligneurs) sur les lots encore aux plages par défaut
      await syncEmptyProductionBatchesModelRanges(caseId, num);

      toast.success("Nombre d'aligneurs enregistré!");
      if (onPhaseUpdated) onPhaseUpdated();
    } catch (err) {
      toast.error("Erreur: " + err.message);
    } finally {
      setSavingAligners(false);
    }
  };

  const getPhaseDesignReviewUrl = useCallback(
    (phase) => String(phase?.design_review_url ?? designUrlInputs[phase?.id] ?? '').trim(),
    [designUrlInputs]
  );

  const openDesignReviewViewer = useCallback(
    (phase) => {
      const url = getPhaseDesignReviewUrl(phase);
      if (!url) return;
      setIframeUrl(url);
      setIframeModalOpen(true);
    },
    [getPhaseDesignReviewUrl]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ACTION HANDLERS MAP (fonction simple — évite closures périmées sur currentUserId)
  // ─────────────────────────────────────────────────────────────────────────
  const buildActionHandlers = (phase, milestone) => ({
      submit_case: () => handleSubmitCase(phase?.id),

      set_completion_type: () => {
        setCompletionTypeMilestone(milestone);
        setCompletionTypePhase(phase);
        setCompletionTypeModalOpen(true);
      },

      open_stl_preview: () => handleOpenStlViewer(),

      open_crm_studio: () =>
        openCrmStudioInNewTab({
          patientId: caseData?.patientIddb,
          caseId,
          settings: crmStudioSettings,
          files: caseFiles,
          toastFn: toast,
        }),

      assign_operator: () => {
        setAssignOperatorPhase(phase);
        setAssignOperatorMilestone(milestone);
        setAssignOperatorModalOpen(true);
      },

      assign_self_case: () => handleAssignSelfCase(milestone, phase),

      assign_prosthetist: () => {
        setAssignModalPhase(phase);
        setAssignModalMilestone(milestone);
        setAssignModalOpen(true);
      },

      assign_package: () => {
        setAssignPackagePhase(phase);
        setAssignPackageMilestone(milestone);
        setAssignPackageModalOpen(true);
      },

      open_design_review: () => openDesignReviewViewer(phase),

      open_ipr_milestone: () => {
        setIprMilestoneId(milestone?.id);
        setIprModalOpen(true);
      },

      view_ipr_sheet: () => {
        window.open(`/ipr-stripping-sheet?caseId=${caseId}`, '_blank');
      },

      open_production_sheet: async () => {
        const group = getGroup(phase);
        const groupNum = parseInt(group);
        let batchId = null;
        if (groupNum >= 2) {
          let { data: batchData } = await supabase
            .from('production_batches').select('id')
            .eq('case_id', caseId).eq('current_phase', group).maybeSingle();
          if (!batchData) {
            const deadlineDate = caseData?.deadline
              ? new Date(caseData.deadline).toISOString()
              : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
            const { fetchConceptionPhaseTotalAligners } = await import(
              '../../../services/caseProductionPhasesService'
            );
            const conceptionAligners =
              phase?.total_aligners ?? (await fetchConceptionPhaseTotalAligners(caseId));
            const half =
              conceptionAligners != null && !Number.isNaN(Number(conceptionAligners))
                ? Math.floor(Number(conceptionAligners) / 2)
                : null;
            const { data: newBatch, error } = await supabase.from('production_batches').insert({
              batch_number:  generateLotBatchNumber(),
              patient_name:  caseData?.patientName || 'N/A',
              clinic_name:   caseData?.clinicName  || 'N/A',
              status: 'draft',
              priority: mapCasePriorityToBatchPriority(caseData?.priority),
              machine_id: caseData?.machineId || null, progress: 0,
              start_date: new Date().toISOString(), deadline: deadlineDate,
              case_id: caseId, current_phase: group,
              ...(half != null
                ? {
                    model_up_from: 0,
                    model_up_to: half,
                    model_low_from: 0,
                    model_low_to: half,
                  }
                : {}),
              notes: `Conception #${caseId} — Lot ${group} créé automatiquement`,
            }).select('id').single();
            if (error) { toast.error('Erreur création lot: ' + error.message); return; }
            batchData = newBatch;
          }
          batchId = batchData.id;
        } else {
          const { data: batchData } = await supabase
            .from('production_batches')
            .select('id')
            .eq('case_id', caseId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          batchId = batchData?.id || null;
        }
        const params = new URLSearchParams({ caseId: String(caseId) });
        if (batchId) params.set('batchId', batchId);
        if (milestone?.id) params.set('milestoneId', milestone.id);
        window.open(`/production-sheet?${params.toString()}`, '_blank');
      },

      add_milestone_note: () => {
        setNoteMilestoneId(milestone?.id);
        setNoteText(milestone?.note ?? '');
        setNoteModalOpen(true);
      },

confirm_milestone: () => {
  if (openMilestoneNameChoiceIfNeeded(milestone, phase, (nameChoice) => {
    handleMilestoneStatusChange(milestone?.id, 'completed', phase, { milestone_name: nameChoice });
  })) return;
  handleMilestoneStatusChange(milestone?.id, 'completed', phase);
},

reject_milestone: () =>
  handleMilestoneStatusChange(milestone?.id, 'rejected', phase),

      confirm_phase: () =>
        setPendingStatus({
          phaseId: phase?.id, phaseOrder: phase?.phase_order,
          group: getGroup(phase), value: 'completed', label: 'Confirmer',
        }),

      reject_phase: () =>
        setPendingStatus({
          phaseId: phase?.id, phaseOrder: phase?.phase_order,
          group: getGroup(phase), value: 'cancelled', label: 'Rejeter',
        }),
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Derived groups
  // ─────────────────────────────────────────────────────────────────────────
  const allItems = useMemo(() => {
    const phaseItems = phases?.items || [];
    return phaseItems
      .filter((phase) => canRoleViewCaseDetailItem(userRole, phase?._caseDetailVisibleRoleTypes))
      .map((phase) => ({
        ...phase,
        milestones: (phase.milestones || []).filter((milestone) =>
          canRoleViewCaseDetailItem(userRole, milestone?._caseDetailVisibleRoleTypes)
        ),
      }));
  }, [phases, userRole]);

  const uniqueGroups = [...new Set(allItems.map((p) => getGroup(p)))]
    .filter((g) => g !== '')
    .sort((a, b) => parseInt(a) - parseInt(b));

  const groups = uniqueGroups.map((groupKey) => {
    const groupPhases = allItems
      .filter((p) => getGroup(p) === groupKey)
      .sort((a, b) => a.phase_order - b.phase_order);
    const groupNum = parseInt(groupKey, 10);
    if (groupNum > 1) {
      const activated = groupPhases.some(p => p.status !== 'pending');
      if (!activated) return null;
    }
    const label = groupNum === 1
      ? 'Lot 1 : Nouvelle commande'
      : `Lot ${groupNum} : Suivi et Continuité du Traitement`;
    return { key: groupKey, label, phases: groupPhases };
  }).filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────
  // Inline extras
  // ─────────────────────────────────────────────────────────────────────────
  const renderInlineExtras = (phase, milestone, actions) => {
    const actionKeys = new Set(actions.map((a) => a.action_key));
    const canEdit =
      !readOnly &&
      (userRole === 'administrator' ||
        userRole === 'dentist_supplier' ||
        isOperatorFamilyRole(userRole));

    return (
      <>
{/* URL field */}
{actionKeys.has('open_design_review') && canEdit && phase?.status === 'in_progress' && (
  <>
    <div className="ml-6 mt-2 flex items-center gap-2 flex-wrap">
      <div className="flex-1 min-w-0 flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-3 py-1.5">
        <Icon name="Link" size={13} className="text-muted-foreground flex-shrink-0" />
        <input
          type="url"
          placeholder="https://exemple.com/lien-conception"
          value={designUrlInputs[phase.id] ?? phase?.design_review_url ?? ''}
          onChange={(e) => setDesignUrlInputs(prev => ({ ...prev, [phase.id]: e.target.value }))}
          className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none min-w-0"
        />
        {(designUrlInputs[phase.id] ?? phase?.design_review_url) && (
          <button
            onClick={() => { setIframeUrl(designUrlInputs[phase.id] ?? phase?.design_review_url); setIframeModalOpen(true); }}
            className="flex-shrink-0 text-sky-400 hover:text-sky-300 transition-colors"
          >
            <Icon name="Eye" size={13} />
          </button>
        )}
      </div>

      {/* ← Modifier vs Enregistrer */}
      {savedDesignUrl[phase.id] ? (
        <button
          onClick={() => setSavedDesignUrl(prev => ({ ...prev, [phase.id]: false }))}
          className={MODIFIER_BUTTON_CLASS}
        >
          <Icon name="Pencil" size={13} /> Modifier URL
        </button>
      ) : (
        <button
          onClick={() => handleSaveDesignUrl(phase.id)}
          disabled={savingUrl}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 disabled:opacity-50 transition-all flex-shrink-0"
        >
          {savingUrl ? <SpinnerIcon /> : <Icon name="Save" size={13} />}
          Enregistrer
        </button>
      )}
    </div>

    {/* Aligners field */}
    <div className="ml-6 mt-1.5 flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-3 py-1.5 min-w-[200px]">
        <Icon name="Layers" size={13} className="text-muted-foreground flex-shrink-0" />
        <input
          type="number" min={1} placeholder="Nombre d'aligneurs total"
          value={alignerInputs[phase.id] ?? (phase?.total_aligners != null ? String(phase.total_aligners) : '')}
          onChange={(e) => setAlignerInputs(prev => ({ ...prev, [phase.id]: e.target.value }))}
          className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none min-w-0"
        />
      </div>

      {/* ← Modifier vs Enregistrer */}
      {savedAligners[phase.id] ? (
        <button
          onClick={() => setSavedAligners(prev => ({ ...prev, [phase.id]: false }))}
          className={MODIFIER_BUTTON_CLASS}
        >
          <Icon name="Pencil" size={13} /> Modifier aligneurs
        </button>
      ) : (
        <button
          onClick={() => handleSaveAligners(phase.id)}
          disabled={savingAligners}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 disabled:opacity-50 transition-all flex-shrink-0"
        >
          {savingAligners ? <SpinnerIcon /> : <Icon name="Save" size={13} />}
          Enregistrer
        </button>
      )}
    </div>
  </>
)}
        {actionKeys.has('add_milestone_note') && milestone?.note && (
          <div className="ml-6 mt-1 flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2">
            <Icon name="StickyNote" size={12} className="flex-shrink-0 mt-0.5" />
            <span className="whitespace-pre-wrap">{milestone.note}</span>
          </div>
        )}
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render milestone row
  // ─────────────────────────────────────────────────────────────────────────
  const renderMilestone = (phase, milestone, idx) => {
    const templateId = milestone?.milestone_template_id;
    const allActions = templateId ? (resolvedTemplateActions[templateId] || []) : [];
    const allowedActions = allActions.filter((row) => {
      if (readOnly && !['view_ipr_sheet', 'open_design_review'].includes(row.action_key)) return false;
      return workflowActionAllowedForAnyRole(effectiveRoleTypes, row.allowed_role_types);
    });
    const handlers = buildActionHandlers(phase, milestone);
    const hasDesignReviewAction = allActions.some((a) => a.action_key === 'open_design_review');
    const hasPackageAction      = allActions.some((a) => a.action_key === 'assign_package');

    const isCompleted = milestone?.completed === true;
    const isRejected  = milestone?.is_rejected === true || milestone?.rejected_date != null;

    let timestampLabel = 'En attente de validation';
    if (isCompleted && milestone?.updated_at) {
      timestampLabel = `Validé le ${new Date(milestone.updated_at).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })}`;
    } else if (isRejected && milestone?.rejected_date) {
      timestampLabel = `Rejeté le ${new Date(milestone.rejected_date).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })}`;
    }

    const nameColorClass = isCompleted
      ? 'text-green-600 dark:text-green-400 font-bold'
      : isRejected ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground';

    const timestampColorClass = isCompleted
      ? 'text-green-600 dark:text-green-400'
      : isRejected ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground';

    const milestoneIconName = isCompleted ? 'CheckCircle2' : isRejected ? 'XCircle' : 'Circle';
    const milestoneIconClass = isCompleted
      ? 'text-green-500 dark:text-green-400'
      : isRejected ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground';

    const savedActions = savedActionsByMilestone[milestone?.id] || new Set();
    const isDentistClient = userRole === 'dentist_client';
    const currentPhaseMilestones = phase?.milestones || [];
    const milestone1 = currentPhaseMilestones.find(m => m.milestone_order === 1);
    const milestone2 = currentPhaseMilestones.find(m => m.milestone_order === 2);
    const ms1And2Confirmed = milestone1?.completed === true && milestone2?.completed === true;
    const canViewDesignClient = isDentistClient && phase?.phase_order === 3 && ms1And2Confirmed;
    const isPhase3Jalon3 = phase?.phase_order === 3 && milestone?.milestone_order === 3;
    const isJalon3LockedForClient = isDentistClient && isPhase3Jalon3 && !ms1And2Confirmed;

    const nameOptions = templateId ? (templateNameOptionsByTemplateId[templateId] || []) : [];
    const displayName = resolveMilestoneDisplayName(milestone, nameOptions);
    const nameChoicePending =
      !isCompleted && isMilestoneNameChoicePending(milestone, nameOptions);

    return (
      <div key={milestone?.id ?? idx} className="flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Icon name={milestoneIconName} size={16} className={`flex-shrink-0 ${milestoneIconClass}`} />
            <span className={`text-sm truncate ${nameColorClass}`}>
              {displayName}
              {nameChoicePending ? (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                  À choisir
                </span>
              ) : null}

              {(() => {
                const outcome = getOutcomeDisplay(milestone?.completion_type);
                if (!outcome) return null;
                return (
                  <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold ${outcome.badgeClass}`}>
                    <Icon name={outcome.icon} size={11} />
                    {outcome.label}
                  </span>
                );
              })()}

              {assignedOperatorName && allActions.some((a) =>
                a.action_key === 'assign_operator' || a.action_key === 'assign_self_case'
              ) && (
  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-400/10 text-blue-500 text-xs font-semibold">
    <Icon name="User" size={11} />
    {assignedOperatorName}
  </span>
)}

{assignedProsthetistName && allActions.some(a => a.action_key === 'assign_prosthetist') && (
  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-500 text-xs font-semibold">
    <Icon name="UserCheck" size={11} />
    {assignedProsthetistName}
  </span>
)}

              {(canViewDesignClient || (!isDentistClient && hasDesignReviewAction)) && phase.total_aligners && milestone?.milestone_order === 1 && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs font-semibold">
                  <Icon name="Layers" size={11} />
                  {phase.total_aligners} aligneurs
                </span>
              )}

              {(canViewDesignClient || (!isDentistClient && hasPackageAction)) && assignedPackageName && hasPackageAction && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-500 text-xs font-semibold">
                  <Icon name="Package" size={11} />
                  {assignedPackageName}
                </span>
              )}
            </span>

            <span
              style={{ fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase', flexShrink: 0 }}
              className={timestampColorClass}
            >
              {timestampLabel}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
            {allowedActions.map((row) => {
              const def      = workflowDefMap[row.action_key];
              const label    = def?.label   || row.action_key;
              const iconName = def?.icon    || null;
              const handler  = handlers[row.action_key];

              if (row.action_key === 'assign_self_case' && !isInitialReviewPhase(phase)) {
                return null;
              }

              if (
                row.action_key === 'assign_self_case' &&
                isInitialReviewPhase(phase) &&
                milestone?.milestone_order !== 1
              ) {
                return null;
              }

              if (row.action_key === 'open_crm_studio' && !isDesignPhaseTimelineItem(phase)) {
                return null;
              }

              if (EDITABLE_AFTER_SAVE_ACTIONS.has(row.action_key)) {
                  // Fiche de production : accessible même après la préproduction (phases suivantes).
                  const requiresInProgress = ![
                    'assign_operator',
                    'assign_prosthetist',
                    'assign_self_case',
                    'open_production_sheet',
                  ].includes(row.action_key);

                if (requiresInProgress && phase?.status !== 'in_progress') return null;
                const alreadySaved = savedActions.has(row.action_key);
                if (alreadySaved) {
                  const modifierLabel =
    row.action_key === 'assign_prosthetist' ? 'Modifier prothésiste' :
    row.action_key === 'assign_operator'    ? 'Modifier opérateur' :
    row.action_key === 'assign_self_case'   ? "M'assigner la commande" :
    row.action_key === 'open_production_sheet' ? 'Fiche de production' :
    'Modifier';
                  return (
                    <button
                      key={`modifier-${milestone?.id}-${row.action_key}`}
                      type="button"
                      onClick={() =>
                        row.action_key === 'assign_self_case'
                          ? handleSelfAssignClick(handler)
                          : handler()
                      }
                      disabled={row.action_key === 'assign_self_case' ? selfAssignBusy : assigningCaseOperator}
                      className={`${
                        row.action_key === 'open_production_sheet'
                          ? actionButtonClass(row.action_key)
                          : MODIFIER_BUTTON_CLASS
                      }${row.action_key === 'assign_self_case' ? selfAssignLockedClass : ''}`}
                      title={
                        row.action_key === 'assign_self_case' && selfAssignLockHint
                          ? selfAssignLockHint
                          : row.action_key === 'open_production_sheet'
                            ? (def?.description || label)
                            : `Modifier — ${def?.description || label}`
                      }
                    >
                      <Icon name={row.action_key === 'open_production_sheet' ? (iconName || 'FileText') : 'Pencil'} size={13} />
                        {modifierLabel}
                    </button>
                  );
                }
                if (!handler) return null;
                return (
                  <button
                    key={`action-${milestone?.id}-${row.action_key}`}
                    type="button"
                    onClick={() =>
                      row.action_key === 'assign_self_case'
                        ? handleSelfAssignClick(handler)
                        : handler()
                    }
                    disabled={row.action_key === 'assign_self_case' ? selfAssignBusy : false}
                    className={`${actionButtonClass(row.action_key)}${row.action_key === 'assign_self_case' ? selfAssignLockedClass : ''}`}
                    title={
                      row.action_key === 'assign_self_case' && selfAssignLockHint
                        ? selfAssignLockHint
                        : def?.description || label
                    }
                  >
                    {iconName && <Icon name={iconName} size={13} />}
                    {label}
                  </button>
                );
              }

              const completionSensitive = ['open_ipr_milestone', 'open_production_sheet'];
              if (completionSensitive.includes(row.action_key) && milestone?.completed) return null;
              if (row.action_key === 'confirm_milestone' && milestone?.completion_type === 'partiel') return null;
              if (row.action_key === 'set_completion_type' && (milestone?.completed || phase?.status !== 'in_progress')) return null;
              if (row.action_key === 'view_ipr_sheet' && !milestone?.completed) return null;


              const phaseDesignReviewUrl = getPhaseDesignReviewUrl(phase);

              if (row.action_key === 'open_design_review') {
                if (!phaseDesignReviewUrl) return null;
                if (isDentistClient) {
                  if (phase?.phase_order !== 3) return null;
                  if (!ms1And2Confirmed) return null;
                }
              }

              if ((row.action_key === 'confirm_milestone' || row.action_key === 'reject_milestone') && isJalon3LockedForClient) return null;

              const phaseActiveRequired = [
                'assign_package',
                'open_ipr_milestone', 'open_production_sheet', 'add_milestone_note',
                'confirm_milestone', 'reject_milestone', 'confirm_phase', 'reject_phase',
                'open_design_review', 'open_crm_studio',
              ];
              if (phaseActiveRequired.includes(row.action_key) && phase?.status !== 'in_progress') {
                const keepDesignReviewVisible =
                  row.action_key === 'open_design_review' && phaseDesignReviewUrl;
                if (!keepDesignReviewVisible) return null;
              }
              if (!handler) return null;

              return (
                <button
                  key={`action-${milestone?.id}-${row.action_key}`}
                  type="button" onClick={handler}
                  className={actionButtonClass(row.action_key)}
                  title={def?.description || label}
                >
                  {iconName && <Icon name={iconName} size={13} />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {isCompleted && milestone?.validatedByName ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 pl-6">
            <Icon name="UserCheck" size={12} className="text-primary shrink-0" />
            Validé par {milestone.validatedByName}
          </p>
        ) : null}

        {renderInlineExtras(phase, milestone, allActions)}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render phase card
  // ─────────────────────────────────────────────────────────────────────────
  const renderPhase = (phase) => {
    const isSubmission = getGroup(phase) === '1' && phase?.phase_order === 1;

    const visibleMilestones = (phase?.milestones ?? []).filter((m) =>
      canRoleViewCaseDetailItem(userRole, m?._caseDetailVisibleRoleTypes)
    );
    const filteredMilestones = isSubmission
      ? visibleMilestones.filter((m) => m.name !== 'Plan de traitement approuvé')
      : visibleMilestones;

    const adminMustUseAssistant =
      !readOnly &&
      isSubmission &&
      isAdminAssistantRole(userRole) &&
      !['submitted', 'completed'].includes(phase?.status);

    const showSubmitButton =
      !readOnly &&
      isSubmission &&
      !isAdminAssistantRole(userRole) &&
      (userRole === 'dentist_client' || isOperatorFamilyRole(userRole)) &&
      !['submitted', 'completed'].includes(phase?.status) &&
      submissionPhotoCheck.ok;

    const showPhaseStatusSelector =
      !readOnly &&
      !isSubmission &&
      (userRole === 'administrator' || isOperatorFamilyRole(userRole)) &&
      phase?.status === 'in_progress' &&
      filteredMilestones.length === 0;

    // Check if inline block should show for this phase
    // ── Auto-detect inline block condition from current milestone state ──────
const phaseMilestones = filteredMilestones;

// Confirm condition: all milestones completed
const allMilestonesCompleted =
  phaseMilestones.length > 0 &&
  phase?.status === 'in_progress' &&
  phaseMilestones.every(m => m.completed === true);

// Reject condition: all milestones rejected
const allMilestonesRejected =
  phaseMilestones.length > 1 &&
  phase?.status === 'in_progress' &&
  phaseMilestones.every(m => m.is_rejected === true);

// pendingConfirmPhase takes priority if explicitly set for this phase,
// otherwise auto-derive from milestone state
const showInlineBlock =
  pendingConfirmPhase?.phase?.id === phase?.id ||
  allMilestonesCompleted ||
  allMilestonesRejected;

// Determine type for auto-derived case
const inlineBlockType = pendingConfirmPhase?.phase?.id === phase?.id
  ? pendingConfirmPhase.type
  : allMilestonesCompleted
    ? 'confirm'
    : 'reject';

// Milestone to use for actions — from pendingConfirmPhase or last milestone
const inlineMilestone = pendingConfirmPhase?.phase?.id === phase?.id
  ? pendingConfirmPhase.milestone
  : allMilestonesCompleted || allMilestonesRejected
    ? phaseMilestones[phaseMilestones.length - 1]
    : null;

    return (
      <div key={phase?.id} className="relative pl-16">
        <div className={`absolute left-0 w-12 h-12 rounded-full flex items-center justify-center ${getPhaseStatusStyle(phase?.status)}`}>
          <Icon name={getPhaseIcon(phase?.type)} size={20} />
        </div>

        <div className="rounded-xl border border-border bg-gradient-to-br from-card via-card to-muted/30 dark:from-card dark:via-card dark:to-muted/20 p-5 shadow-[0_12px_40px_-16px_rgb(15_23_42_/_0.12),0_4px_14px_-6px_rgb(15_23_42_/_0.07)] dark:shadow-[0_12px_40px_-16px_rgb(0_0_0_/_0.45),0_4px_14px_-6px_rgb(0_0_0_/_0.28)]">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-base font-semibold text-foreground">{phase?.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{phase?.description}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPhaseStatusStyle(phase?.status)}`}>
              {phase?.statusLabel}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Date de début</p>
                <p className="text-sm font-medium text-foreground">
                  {isSubmission ? caseData?.createdDate : phase?.startDate}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CalendarCheck" size={16} className="text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Date de fin</p>
                <p className="text-sm font-medium text-foreground">{phase?.endDate || 'Non confirmée'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="User" size={16} className="text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Responsable</p>
                <p className="text-sm font-medium text-foreground">{phase?.responsible || '—'}</p>
              </div>
            </div>
          </div>

          {phase?.notes && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-foreground">{phase?.notes}</p>
            </div>
          )}

          {filteredMilestones.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Objectifs</h4>
              {isInitialReviewPhase(phase) && isSelfAssignLocked && (
                <p className="mb-3 text-xs text-warning flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
                  <Icon name="Lock" size={13} className="shrink-0 mt-0.5" />
                  <span>
                    {selfAssignLockHint}{' '}
                    <button
                      type="button"
                      className="font-medium text-primary hover:underline"
                      onClick={() => navigate(`/case-detail/${activeSelfAssignment.caseId}`)}
                    >
                      Ouvrir la commande active
                    </button>
                  </span>
                </p>
              )}
              <div className="space-y-3">
                {filteredMilestones.map((milestone, idx) => renderMilestone(phase, milestone, idx))}
              </div>
              {(userRole === 'dentist_supplier' || userRole === 'administrator' || isOperatorFamilyRole(userRole)) &&
                phase?.status === 'in_progress' && !isSubmission && (
                  <p className="mt-2 text-xs text-muted-foreground italic">
                  </p>
                )}
            </div>
          )}

          {showPhaseStatusSelector && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <label className="block text-sm font-medium text-foreground mb-3">
                Mettre à jour le statut de la phase
              </label>
              <div className="flex justify-center gap-2 flex-wrap">
                {[
                  { value: 'completed', label: 'Confirmer', color: 'text-green-600 border-green-400 hover:bg-green-50 dark:hover:bg-green-950' },
                  { value: 'cancelled', label: 'Rejeter',   color: 'text-red-600 border-red-400 hover:bg-red-50 dark:hover:bg-red-950' },
                ].map(({ value, label, color }) => (
                  <button
                    key={value}
                    onClick={() => setPendingStatus({ phaseId: phase?.id, phaseOrder: phase?.phase_order, group: getGroup(phase), value, label })}
                    disabled={updatingPhaseId === phase?.id}
                    className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition-all duration-150 active:scale-95 disabled:opacity-40 ${color}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {adminMustUseAssistant && (
            <div className="flex flex-col items-center gap-2 mt-6">
              <p className="text-xs text-muted-foreground text-center px-4">
                Complétez l&apos;envoi guidé (5 étapes) pour soumettre cette commande au nom du
                dentiste.
              </p>
              <button
                type="button"
                onClick={() => navigate(getCaseAssistantPath(caseId))}
                className="relative flex items-center gap-2.5 px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-primary hover:bg-primary/90 active:scale-95 transition-all duration-150 shadow-md"
              >
                <Icon name="Send" size={16} />
                Ouvrir l&apos;envoi guidé
              </button>
            </div>
          )}

          {showSubmitButton && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => handleSubmitCase(phase?.id)}
                disabled={isSubmitting || !submissionPhotoCheck.ok}
                className="relative flex items-center gap-2.5 px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-primary hover:bg-primary/90 active:scale-95 disabled:opacity-60 transition-all duration-150 shadow-md"
              >
                {isSubmitting
                  ? <><SpinnerIcon className="text-white" /> Envoi en cours...</>
                  : <><Icon name="Send" size={16} /> Envoyer Conception</>
                }
              </button>
            </div>
          )}

          {isSubmission &&
            (['dentist_client', 'administrator'].includes(userRole) || isOperatorFamilyRole(userRole)) &&
            !['submitted', 'completed'].includes(phase?.status) &&
            !submissionPhotoCheck.ok && (
              <p className="text-xs text-muted-foreground text-center mt-3 px-4">
                {submissionPhotoCheck.missingKeys.length} photo
                {submissionPhotoCheck.missingKeys.length > 1 ? 's' : ''} obligatoire
                {submissionPhotoCheck.missingKeys.length > 1 ? 's' : ''} manquante
                {submissionPhotoCheck.missingKeys.length > 1 ? 's' : ''} — téléversez les 10 photos
                cliniques dans l’onglet Fichiers avant l’envoi.
              </p>
            )}

  

        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {readOnly ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-muted-foreground">
          La commande est annulée. Les phases et jalons sont conservés en lecture seule.
        </div>
      ) : null}
      {isSelfAssignLocked ? (
        <div className="rounded-lg border border-warning/50 bg-warning/15 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={18} className="text-warning shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              {selfAssignLockHint}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/case-detail/${activeSelfAssignment.caseId}`)}
            className="text-sm font-medium text-primary hover:underline whitespace-nowrap shrink-0"
          >
            Voir la commande active
          </button>
        </div>
      ) : null}

      {userRole === 'dentist_client' && workflowProgress ? (
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-2">
          <CaseWorkflowProgress
            variant="header"
            percent={workflowProgress.percent}
            macroLabel={workflowProgress.macroLabel}
            activeGroupNum={workflowProgress.activeGroupNum}
            totalLots={workflowProgress.totalLots}
            lots={workflowProgress.lots}
          />
          <p className="text-xs text-muted-foreground">{t('caseWorkflowProgress.timelineHint')}</p>
        </div>
      ) : null}

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon name="Clock" size={16} />
        <span>Date de création : {caseData?.createdDate}</span>
      </div>

      {groups.length === 0 && (
        <div className="p-4 rounded-lg border border-destructive/40 bg-destructive/10 text-sm text-destructive">
          ⚠️ Aucune phase avec un groupe défini.
        </div>
      )}

      <div className="space-y-8">
        {groups.map((group) => (
          <div key={group.key}>
            <div className="mb-4 rounded-xl border border-border/80 bg-gradient-to-r from-primary/[0.06] via-card to-muted/25 dark:from-primary/[0.1] dark:via-card dark:to-muted/20 px-4 py-3 shadow-sm">
              <h2 className="text-lg font-semibold text-foreground">{group.label}</h2>
            </div>
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
              <div className="space-y-6">
                {group.phases.map((phase) => renderPhase(phase))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Phase status confirmation modal (for confirm_phase / reject_phase buttons) */}
      {pendingStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-base font-semibold text-foreground mb-1">Confirmer le changement</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Voulez-vous mettre à jour le statut vers{' '}
              <span className="font-medium text-foreground">« {pendingStatus.label} »</span> ?
              {pendingStatus.group === '1' && pendingStatus.phaseOrder === 3 && pendingStatus.value === 'completed' && (
                <span className="block mt-2 text-xs text-info">Un lot de production sera automatiquement créé.</span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingStatus(null)}
                className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  handlePhaseStatusChange(pendingStatus.phaseId, pendingStatus.value, pendingStatus.phaseOrder, pendingStatus.group);
                  setPendingStatus(null);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Iframe viewer ────────────────────────────────────────────────── */}
      {iframeModalOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/70 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-background border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              <Icon name="ExternalLink" size={15} className="text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium text-foreground truncate">{iframeUrl}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href={iframeUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                <Icon name="SquareArrowOutUpRight" size={13} />
                Ouvrir dans un onglet
              </a>
              <button
                onClick={() => setIframeModalOpen(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/20 transition-colors"
              >
                <Icon name="X" size={13} />
                Fermer
              </button>
            </div>
          </div>
          <div className="flex-1 relative">
            <iframe
              src={iframeUrl} title="Aperçu de la conception"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </div>
        </div>
      )}

      {/* ── Note modal ───────────────────────────────────────────────────── */}
      {noteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-md mx-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="StickyNote" size={18} className="text-orange-400" />
              <h3 className="text-base font-semibold text-foreground">Ajouter une note</h3>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Saisissez votre note ici..."
              rows={5}
              className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none focus:border-primary transition-colors"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setNoteModalOpen(false); setNoteText(''); setNoteMilestoneId(null); }}
                className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveNote}
                disabled={savingNote}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 disabled:opacity-50 transition-all"
              >
                {savingNote ? <SpinnerIcon /> : <Icon name="Save" size={14} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sub-modals ───────────────────────────────────────────────────── */}
      <StlViewerModal
        isOpen={stlViewerOpen}
        onClose={() => setStlViewerOpen(false)}
        stlFiles={stlViewerFiles}
      />

      <IPRMilestoneModal
        isOpen={iprModalOpen}
        onClose={() => { setIprModalOpen(false); setIprMilestoneId(null); }}
        caseId={caseId}
        milestoneId={iprMilestoneId}
        onSaved={() => {
          if (iprMilestoneId) markActionSaved(iprMilestoneId, 'open_ipr_milestone');
          if (onPhaseUpdated) onPhaseUpdated();
        }}
      />

      <AssignUserModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onAssign={handleAssignProsthetist}
        roleType="dentist_supplier"
        title="Assigner un prothésiste"
        description="Sélectionnez le prothésiste à assigner à cette conception."
      />

      <AssignUserModal
        isOpen={assignOperatorModalOpen}
        onClose={() => {
          setAssignOperatorModalOpen(false);
          setAssignOperatorPhase(null);
        }}
        onAssign={handleAssignOperator}
        roleType={getAssignOperatorRoleTypesForPhase(assignOperatorPhase)}
        title={assignOperatorModalCopy.title}
        description={assignOperatorModalCopy.description}
        allowForce={isAdministratorActor(effectiveRoleTypes)}
        assignmentContext="initial_review"
      />

      <AssignUserModal
        isOpen={assignPhaseResponsibleOpen}
        onClose={() => {
          setAssignPhaseResponsibleOpen(false);
          setAssignPhaseResponsibleTarget(null);
        }}
        onAssign={(userId, _fullName, opts = {}) =>
          handleAssignPhaseResponsible(userId, opts)
        }
        roleType={
          assignPhaseResponsibleTarget
            ? [getEligibleOperatorRoleForPhaseName(assignPhaseResponsibleTarget.name)].filter(Boolean)
            : 'operator_3d'
        }
        title="Assigner le responsable de phase"
        description={
          assignPhaseResponsibleTarget
            ? `Sélectionnez l'opérateur pour la phase « ${assignPhaseResponsibleTarget.name} ».`
            : 'Sélectionnez un opérateur.'
        }
        allowForce={isAdministratorActor(effectiveRoleTypes)}
        assignmentContext="workshop"
      />

      <AssignPackageMilestoneModal
        isOpen={assignPackageModalOpen}
        onClose={() => { setAssignPackageModalOpen(false); setAssignPackagePhase(null); setAssignPackageMilestone(null); }}
        dentistId={caseData?.dentistId}
        caseId={caseId}
        onSuccess={handlePackageAssigned}
      />

      {milestoneNameChoiceModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ListChecks" size={18} className="text-primary" />
              <h3 className="text-base font-semibold text-foreground">Choisir une valeur</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Sélectionnez la valeur pour le jalon{' '}
              <span className="font-medium text-foreground">
                {milestoneNameChoiceModal.milestone?.name}
              </span>.
            </p>
            <MilestoneNameChoiceField
              options={milestoneNameChoiceModal.options}
              value={pendingMilestoneNameChoice}
              onChange={setPendingMilestoneNameChoice}
            />
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => {
                  setMilestoneNameChoiceModal(null);
                  setPendingMilestoneNameChoice('');
                }}
                className="flex-1 px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={!pendingMilestoneNameChoice}
                onClick={() => {
                  try {
                    const name = assertValidMilestoneNameChoice(
                      pendingMilestoneNameChoice,
                      milestoneNameChoiceModal.options
                    );
                    milestoneNameChoiceModal.onConfirmed?.(name);
                    setMilestoneNameChoiceModal(null);
                    setPendingMilestoneNameChoice('');
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Completion Type Modal ─────────────────────────────────────── */}
      {completionTypeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ToggleLeft" size={18} className="text-purple-400" />
              <h3 className="text-base font-semibold text-foreground">Type de complétion</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Sélectionnez le type de complétion pour le jalon{' '}
              <span className="font-medium text-foreground">{completionTypeMilestone?.name}</span>.
              {completionTypeMilestone?.completion_type === 'partiel' && (
                <span className="block mt-2 text-xs text-amber-500 font-medium">
                  ⚠️ Un jalon partiel ne peut pas être validé.
                </span>
              )}
            </p>
            <div className="flex gap-2 mb-5">
              {[
                { type: 'complet', label: 'Complète', icon: 'CheckCircle2', active: 'border-green-500 bg-green-500/10 text-green-600 dark:text-green-400', hover: 'hover:border-green-400 hover:bg-green-400/5' },
                { type: 'partiel', label: 'Partiel', icon: 'CircleDashed', active: 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400', hover: 'hover:border-amber-400 hover:bg-amber-400/5' },
                { type: 'echec', label: 'Échec', icon: 'XCircle', active: 'border-red-500 bg-red-500/10 text-red-600 dark:text-red-400', hover: 'hover:border-red-400 hover:bg-red-400/5' },
              ].map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  onClick={async () => {
                    const now = new Date().toISOString();
                    const { data: { user } } = await supabase.auth.getUser();
                    const row = { completion_type: opt.type, updated_at: now };
                    if (user?.id) {
                      row.validated_by_user_id = user.id;
                      row.validated_at = now;
                    }
                    await supabase.from('phase_milestones').update(row).eq('id', completionTypeMilestone?.id);
                    setCompletionTypeModalOpen(false);
                    if (onPhaseUpdated) onPhaseUpdated();
                  }}
                  className={`flex-1 flex flex-col items-center gap-2 px-3 py-3 rounded-lg border-2 transition-all ${
                    completionTypeMilestone?.completion_type === opt.type
                      ? opt.active
                      : `border-border text-muted-foreground ${opt.hover}`
                  }`}
                >
                  <Icon name={opt.icon} size={22} />
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setCompletionTypeModalOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tiny spinner ──────────────────────────────────────────────────────────────
const SpinnerIcon = ({ className = '' }) => (
  <svg
    className={`animate-spin h-3.5 w-3.5 ${className}`}
    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

export default PhasesTimelineTab;